import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import {
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@monksflow/monks-ui';
import { PlusIcon, RefreshCwIcon, ShieldCheck } from 'lucide-react';
import {
  deleteMediaGuideline,
  editableVersion,
  MEDIA_GUIDELINE_QUERY_KEYS,
  mediaGuidelinesQueryOptions,
  publishGuideline,
  retireGuideline,
  type MediaGuideline,
} from '@/entities/media-guideline';
import {
  GuidelineEditorDialog,
  GuidelinePlatformNav,
  MediaGuidelinesTable,
  PlatformRulesPanel,
} from '@/features/media-guidelines';
import { notificationService } from '@/lib/services/notification.service';
import { StaggerItem } from '@/shared/ui/animations';
import {
  SectionPageHeader,
  SectionPageHeaderBackButton,
  SectionPageHeaderContent,
  SectionPageHeaderDescription,
  SectionPageHeaderIcon,
  SectionPageHeaderTitle,
  ListPageLayout,
} from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import { SearchInput } from '@/shared/ui/SearchInput/SearchInput';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { useTranslation } from 'react-i18next';

/**
 * Media Guidelines: the house standards Delivery's pre-flight checks a plan against,
 * and below them the platform rules it always checks first.
 *
 * Platform truth stays read-only. A workspace that switched one off would not change
 * the platform's mind — the plan would pass pre-flight and fail at publish with a
 * worse message, after the objects it would have stopped were already live. Listing
 * it is the part that was missing: it used to be legible only in the source.
 */
export function MediaGuidelines() {
  const { t } = useTranslation('knowledge');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<MediaGuideline | undefined>(undefined);
  /** Which platform's page is open; null is the cross-platform one. */
  const [platform, setPlatform] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  // Debounced before it reaches the query: the raw value is both the request and
  // the cache key, so typing a word otherwise fires a request and creates a cache
  // entry per keystroke, flashing the table skeleton each time.
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const { data, isLoading, isError, isPlaceholderData, refetch } = useQuery({
    // Each platform is its own cache entry, so switching would otherwise drop the
    // table to a skeleton on every first visit. The rows carried over are the previous
    // platform's, so they are dimmed and marked busy below — unlabelled, they would be
    // another platform's standards read as this one's.
    placeholderData: keepPreviousData,
    ...mediaGuidelinesQueryOptions({
      pageSize: 100,
      search: debouncedSearch.trim() || undefined,
      // Scoping to a platform still returns the cross-platform standards, which the
      // server does deliberately: they run against that platform too, and a list
      // that hid them would misstate what is enforced.
      ...(platform ? { platform: [platform] } : {}),
    }),
  });

  const act = useMutation({
    mutationFn: async (task: {
      kind: 'publish' | 'retire' | 'delete';
      guideline: MediaGuideline;
    }) => {
      if (task.kind === 'delete') {
        await deleteMediaGuideline(task.guideline.id);
        return t('mediaGuidelines.mediaGuidelines.guidelineDeleted');
      }
      if (task.kind === 'retire') {
        // The reason is mandatory and lands in the audit trail. A prompt stands in
        // for the confirm dialog this still needs.
        const reason = window
          .prompt(t('mediaGuidelines.mediaGuidelines.whyIsThisNoLongerEnforced'))
          ?.trim();
        if (!reason) return null;
        await retireGuideline(task.guideline.id, 'superseded', reason);
        return t('mediaGuidelines.mediaGuidelines.guidelineRetired');
      }
      const reason = window
        .prompt(t('mediaGuidelines.mediaGuidelines.whyAreYouPublishingThisVersion'))
        ?.trim();
      if (!reason) return null;
      await publishGuideline(task.guideline.id, reason);
      return t('mediaGuidelines.mediaGuidelines.guidelinePublished');
    },
    onSuccess: async (message) => {
      if (!message) return;
      await queryClient.invalidateQueries({ queryKey: MEDIA_GUIDELINE_QUERY_KEYS.all });
      notificationService.showSuccess(message);
    },
    onError: (error) => {
      const { detail } = parseProblemDetail(
        error,
        t('mediaGuidelines.mediaGuidelines.couldNotUpdateTheGuideline'),
      );
      notificationService.showError(detail);
    },
  });

  const openEditor = (guideline?: MediaGuideline) => {
    setEditing(guideline);
    setEditorOpen(true);
  };

  const rows = data?.items ?? [];

  return (
    <ListPageLayout>
      <StaggerItem index={0}>
        <SectionPageHeader variant="compact">
          <SectionPageHeaderBackButton
            onClick={() => void navigate(ROUTES.KNOWLEDGE_HUB)}
            aria-label={t('mediaGuidelines.mediaGuidelines.backToKnowledge')}
          />
          <SectionPageHeaderIcon icon={ShieldCheck} />
          <SectionPageHeaderContent>
            <SectionPageHeaderTitle>
              {t('mediaGuidelines.mediaGuidelines.mediaGuidelines')}
            </SectionPageHeaderTitle>
            <StaggerItem index={1} variant="fade">
              <SectionPageHeaderDescription>
                {t('mediaGuidelines.mediaGuidelines.setTheNamingBudgetAndPlatform')}
              </SectionPageHeaderDescription>
            </StaggerItem>
          </SectionPageHeaderContent>
        </SectionPageHeader>
      </StaggerItem>

      {/* Two frames rather than one surface, with a gap between them: the rail is a
          different thing from the page it selects, and `ListPageBody` is explicitly a
          single `overflow-hidden` card — which also left the content with nowhere to
          scroll once a platform page grew past the viewport. */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row">
        <StaggerItem index={2} className="md:h-full md:shrink-0">
          <GuidelinePlatformNav value={platform} onChange={setPlatform} />
        </StaggerItem>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto rounded-lg bg-card p-4 text-foreground md:p-6">
          <StaggerItem index={3}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder={t('mediaGuidelines.mediaGuidelines.searchGuidelinesPlaceholder')}
                className="w-full sm:w-80"
                size="sm"
                aria-label={t('mediaGuidelines.mediaGuidelines.searchGuidelines')}
              />
              <Button onClick={() => openEditor(undefined)}>
                <PlusIcon className="mr-2 size-4" />
                {t('mediaGuidelines.mediaGuidelines.newGuideline')}
              </Button>
            </div>
          </StaggerItem>

          <StaggerItem index={4}>
            {/* Checked before the table renders: `rows` falls back to an empty array, so a
                failed request would otherwise reach the "No guidelines yet" empty state —
                on the page that answers which standards are enforced, with no alert and
                nothing to retry with. */}
            {isError ? (
              <Empty role="alert" data-testid="media-guidelines-load-error">
                <EmptyHeader>
                  <EmptyTitle>
                    {t('mediaGuidelines.mediaGuidelines.couldNotLoadGuidelines')}
                  </EmptyTitle>
                  <EmptyDescription>
                    {t('mediaGuidelines.mediaGuidelines.guidelinesAreStillThere')}
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Button variant="outline" onClick={() => void refetch()}>
                    <RefreshCwIcon className="mr-2 size-4" />
                    {t('mediaGuidelines.mediaGuidelines.tryAgain')}
                  </Button>
                </EmptyContent>
              </Empty>
            ) : (
              /* Dimmed while the rows on screen belong to the platform just left. They
                 are real guidelines, so unmarked they read as this platform's own. */
              <div
                aria-busy={isPlaceholderData}
                className={
                  isPlaceholderData
                    ? 'pointer-events-none opacity-60 transition-opacity'
                    : undefined
                }
              >
                <MediaGuidelinesTable
                  rows={rows}
                  isLoading={isLoading}
                  onEdit={openEditor}
                  onPublish={(guideline) => {
                    const version = editableVersion(guideline);
                    if (version?.status !== 'draft') {
                      notificationService.showError(
                        t('mediaGuidelines.mediaGuidelines.thereIsNoOpenDraftToPublish'),
                      );
                      return;
                    }
                    act.mutate({ kind: 'publish', guideline });
                  }}
                  onRetire={(guideline) => act.mutate({ kind: 'retire', guideline })}
                  onDelete={(guideline) => act.mutate({ kind: 'delete', guideline })}
                />
              </div>
            )}
          </StaggerItem>

          {/* Below the editable set, because that is the reading order: these run
              whether or not a workspace writes anything, and are the floor the
              guidelines above are added to. */}
          <StaggerItem index={5}>
            <PlatformRulesPanel platform={platform} />
          </StaggerItem>
        </div>
      </div>

      {editorOpen && (
        <GuidelineEditorDialog
          // Remounting on the edited id gives the form fresh state per open, so no
          // reset effect is needed.
          key={editing?.id ?? 'new'}
          open={editorOpen}
          guideline={editing}
          onClose={() => {
            setEditorOpen(false);
            setEditing(undefined);
          }}
        />
      )}
    </ListPageLayout>
  );
}
